import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, Plane } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AirlineResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '10', 10);
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios.post(`${API}/airline/results`, { player_name: localStorage.getItem('gaurik_player_name') || 'Player', score, total_questions: total }).catch(console.error);
    }
    if (percentage >= 50) {
      const end = Date.now() + 2500;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#0ea5e9', '#6366f1', '#f43f5e'] });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#0ea5e9', '#6366f1', '#f43f5e'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, score, total]);

  const getMessage = () => {
    if (percentage >= 90) return 'AVIATION EXPERT!';
    if (percentage >= 70) return 'GREAT SPOTTER!';
    if (percentage >= 50) return 'NICE FLYING!';
    return 'KEEP SPOTTING!';
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-sky-100 to-white">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        <Plane className="w-12 h-12 mx-auto mb-3 text-sky-500" />

        <h1 data-testid="airline-results-message" className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-1">
          {getMessage()}
        </h1>
        <p className="font-body text-sm text-gray-500 mb-5">
          {percentage >= 70 ? 'You really know your airlines!' : 'Every quiz makes you sharper!'}
        </p>

        <div className="flex justify-center gap-3 mb-5" data-testid="airline-results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        <p data-testid="airline-results-score" className="font-heading text-5xl font-black text-gray-900 mb-1">
          {score}/{total}
        </p>
        <p className="font-body text-base font-bold text-gray-500 mb-6">{percentage}% correct</p>

        <div className="flex flex-col gap-3">
          <button
            data-testid="airline-play-again"
            onClick={() => navigate('/airline-quiz/play')}
            className="bg-sky-500 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-sky-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-sky-400 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="airline-go-home"
            onClick={() => navigate('/')}
            className="bg-gray-800 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-gray-950 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-gray-700 flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
